import { IoAdd } from "react-icons/io5"
import Avatar from "../shared/Avatar"
import { useAvatarSrc } from "../settings/useAvatarSrc"
import { useCallback, useEffect, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { createUser, fetchAllUsers, fetchAuthToken, updateUserRoles } from "../../utils/apiUtils";
import debounce from "lodash.debounce";
import ReactJson from "react-json-view";
import { humanizeTimeDifference } from "../../utils/commonUtils";
import Select from 'react-select';
import { queryClient } from "../../App";
import Modal from "../modal/Modal";
import { ROLE_OPTIONS } from "../../utils/roleOptions";
import AccessTabs from "./AccessTabs";
import "./Access.scss";

const UserAvatar = ({ email, initials, src, color, avatarKey }) => {
    const avatar = useAvatarSrc(email, { src, color, key: avatarKey });
    return (
        <Avatar
            src={avatar.src}
            initials={initials}
            backgroundColor={avatar.color || "var(--darkGrey)"}
            color={"var(--white)"}
        />
    );
};

const AccessDashboard = () => {

    const [searchValue, setSearchValue] = useState('');
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();
    const [selectedUser, setSelectedUser] = useState()
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [newUserEmail, setNewUserEmail] = useState('')
    const [newUserRoles, setNewUserRoles] = useState([])
    const [createError, setCreateError] = useState()

    const [scrollRoot, setScrollRoot] = useState(null);
    const { ref, inView } = useInView({
        root: scrollRoot,
        rootMargin: "160px",
        threshold: 0,
    });

    const queryKey = ['access', decouncedSearchValue];

    const {
        data,
        fetchNextPage,
        isLoading,
        hasNextPage,
        isFetchingNextPage,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchOnWindowFocus: true,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = {};

            if (pageParam) {
                params.lastEvaluatedKey = pageParam;
            }

            if (decouncedSearchValue) {
                params.emailPrefix = decouncedSearchValue
            }

            return fetchAllUsers(params);
        },
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => {
            return lastPage?.LastEvaluatedKey
                ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey))
                : undefined;
        },
        staleTime: 0,
        enabled: true
    })

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const debouncedChangeHandler = useCallback(
        debounce((value) => {
            setDecouncedSearchValue(value?.trim().toLowerCase() || undefined);
        }, 500),
        []
    );

    const mutation = useMutation({
        mutationFn: updateUserRoles,
        onSuccess: (data, variables) => {

            if (!data) return

            queryClient.setQueryData(queryKey, (oldData => {
                if (oldData) {
                    for (let i = 0; i < oldData.pages.length; i++) {
                        let page = oldData.pages[i];
                        page.Items = page.Items.map((item) => {
                            if (item.pk === variables.email) {
                                item = { ...item, ...data }
                            }
                            return item;
                        });

                        oldData.pages[i] = page;
                    }
                }
                return oldData
            }));
        },
        onError: (err) => {
            console.log(err)
        }
    })

    const createUserMutation = useMutation({
        mutationFn: createUser,
        onSuccess: (data) => {
            setIsCreateModalOpen(false)
            setNewUserEmail('')
            setNewUserRoles([])
            setCreateError()
            setSelectedUser(data)
            queryClient.invalidateQueries({ queryKey: ['access'] })
        },
        onError: (err) => {
            setCreateError(err?.message || 'Failed to create user')
        }
    })

    const results = (data?.pages || []).flatMap((page) => page.Items || [])
    const currentlySelectedUser = results?.find(r => r.pk === selectedUser?.pk) || selectedUser;


    const { data: tokenData } = useQuery({
        queryKey: ['fetchAuthToken', currentlySelectedUser?.pk],
        queryFn: () => fetchAuthToken({ pk: currentlySelectedUser?.pk }),
        enabled: !!currentlySelectedUser?.pk
    })

    const getLatestDate = (isoDates) => {
        const latestDate = isoDates.filter(d => d).reduce((latest, current) => {
            return new Date(current) > new Date(latest) ? current : latest;
        });
        return latestDate
    }

    const openCreateModal = () => {
        setNewUserEmail('')
        setNewUserRoles([])
        setCreateError()
        setIsCreateModalOpen(true)
    }

    const handleCreateUser = (e) => {
        e.preventDefault()
        setCreateError()

        const email = newUserEmail.trim().toLowerCase()
        if (!email) {
            setCreateError('Email is required')
            return
        }
        if (!newUserRoles.length) {
            setCreateError('Select at least one role')
            return
        }

        createUserMutation.mutate({
            email,
            roles: newUserRoles.map(role => role.value),
        })
    }

    return (
        <div className="container">

            <p className='title'>Access Management</p>
            <p className="sub-title">Configure your team, users and roles</p>
            <AccessTabs />

            <div className="access-management">
                <div className="details">
                    <div className="access-user-pane">
                        <div className="access-user-list">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 1rem', borderBottom: '1px solid var(--lightGrey)' }}>
                                <div style={{ flex: 1, }}>
                                    <p className="access-management-title">Users</p>
                                </div>

                                <div>
                                    <IoAdd
                                        size={'1.4rem'}
                                        color={'var(--blue)'}
                                        style={{ cursor: 'pointer' }}
                                        onClick={openCreateModal}
                                        title="Create user"
                                    />
                                </div>
                            </div>

                            <div className="access-search">
                                <input
                                    type="search"
                                    placeholder="Search by email..."
                                    value={searchValue}
                                    autoComplete="off"
                                    onChange={(e) => {
                                        setSearchValue(e.target.value);
                                        debouncedChangeHandler(e.target.value);
                                    }}
                                />
                            </div>

                            <ul
                                className="scrollable access-user-scroll"
                                ref={setScrollRoot}
                            >


                                {isLoading ? (
                                    <div style={{ width: "100%", minWidth: '28rem', padding: "1rem 1rem", borderRadius: "0rem", height: '3rem' }} className="loading"></div>

                                ) : status === "error" ? (
                                    <span className='danger'>Error: {error.message}</span>
                                ) : (
                                    <>
                                        {results?.map(user => {
                                            const [firstName, lastName] = user.pk?.split('@')[0].split('.')
                                            return <li className={`user ${selectedUser?.pk === user.pk && 'selected'}`} key={user.pk} onClick={() => setSelectedUser(user)}>
                                                <UserAvatar
                                                    email={user.pk}
                                                    src={user.avatarUrl}
                                                    avatarKey={user.avatarKey}
                                                    color={user.color}
                                                    initials={firstName?.slice(0, 1) + lastName?.slice(0, 1)}
                                                />
                                                <div className="user-info">
                                                    <div>
                                                        <p className="user-name">{`${firstName} ${lastName}`}</p>
                                                        <p>{user.pk}</p>
                                                        <div className="roles">{user.roles?.map(role => <p key={role} className="role">{role}</p>)}</div>
                                                    </div>



                                                    <p className="sub-title last-login">{(user.tokenRefreshedAt || user.loggedInAt) && humanizeTimeDifference(getLatestDate([user.tokenRefreshedAt, user.loggedInAt]))}</p>
                                                </div>


                                            </li>
                                        }
                                        )
                                        }


                                        <div style={{ padding: "1rem 0rem", display: isFetchingNextPage || hasNextPage ? "block" : "none" }}>
                                            <button
                                                ref={ref}
                                                onClick={() => fetchNextPage()}
                                                disabled={!hasNextPage || isFetchingNextPage}
                                                className="clickable-link"
                                                style={{ padding: ".4rem 1rem", backgroundColor: "var(--white)", border: "none" }}
                                            >
                                                {isFetchingNextPage ? "Loading more..." : hasNextPage ? "Load more" : "Done"}
                                            </button>
                                        </div>
                                    </>
                                )}

                            </ul>

                        </div>



                        {currentlySelectedUser &&
                            <div style={{ padding: '1rem', flex: 1 }}>

                                <p style={{ fontSize: '1.2rem', fontWeight: 600, textTransform: 'capitalize' }}>{selectedUser?.pk.split('@')[0].split('.').join(' ')}</p>

                                <p style={{ fontWeight: '600', padding: '1rem 0rem .4rem 0rem' }}>Roles</p>
                                <Select
                                    name={'roles'}
                                    className="react-select-container large-input"
                                    classNamePrefix="react-select"
                                    placeholder={'Select roles...'}
                                    isMulti={true}
                                    value={currentlySelectedUser?.roles ? currentlySelectedUser.roles?.map(role => ({ value: role, label: role })) : null}
                                    options={ROLE_OPTIONS}
                                    onChange={(val) => {
                                        const email = selectedUser.pk
                                        const roles = val.map(val => val.value)

                                        mutation.mutate({ email, roles })

                                    }}
                                />
                                <ReactJson
                                    style={{ padding: "1rem" }}
                                    src={{ ...selectedUser, token: tokenData?.Item }}
                                    name={false}
                                    collapsed={false}
                                    collapseStringsAfterLength={150}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                    sortKeys={false}
                                />
                            </div>
                        }

                    </div>

                </div>
            </div>

            <Modal
                isOpen={isCreateModalOpen}
                className="modal-content-compact"
                onClose={() => {
                    if (!createUserMutation.isPending) {
                        setIsCreateModalOpen(false)
                    }
                }}
            >
                <form onSubmit={handleCreateUser} className="form" style={{ padding: '1.75rem', width: '100%', boxSizing: 'border-box' }}>
                    <p className="title" style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>Create user</p>
                    <p className="sub-title" style={{ marginBottom: '1.25rem' }}>Add a new user and assign roles</p>

                    <div className="form-input" style={{ paddingBottom: '1rem' }}>
                        <label>Email</label>
                        <input
                            type="email"
                            placeholder="first.last@centivo.com"
                            value={newUserEmail}
                            onChange={(e) => setNewUserEmail(e.target.value)}
                            disabled={createUserMutation.isPending}
                            autoFocus
                            autoComplete="off"
                            style={{ height: '48px' }}
                        />
                    </div>

                    <div className="form-input" style={{ paddingBottom: createError ? '0.5rem' : '0.25rem' }}>
                        <label>Roles</label>
                        <Select
                            name="new-user-roles"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="Select roles..."
                            isMulti
                            options={ROLE_OPTIONS}
                            value={newUserRoles}
                            onChange={(val) => setNewUserRoles(val || [])}
                            isDisabled={createUserMutation.isPending}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 200 }),
                            }}
                        />
                    </div>

                    {createError && <p className="danger" style={{ padding: '0 0 0.75rem' }}>{createError}</p>}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
                        <button
                            type="button"
                            className="btn outline-btn"
                            disabled={createUserMutation.isPending}
                            onClick={() => setIsCreateModalOpen(false)}
                        >
                            Cancel
                        </button>
                        {createUserMutation.isPending ? (
                            <div className="loader" />
                        ) : (
                            <input className="btn" type="submit" value="Create" />
                        )}
                    </div>
                </form>
            </Modal>

        </div>
    )
}

export default AccessDashboard
